import { Auth0AuthProvider } from "./auth0AuthProvider";
import {
  Auth0Client,
  ICache,
  type RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import { fetchUtils, type AuthProvider } from "ra-core";
import { ManagementClient } from "auth0";
import {
  getSelectedDomainFromStorage,
  getClientIdFromStorage,
  getDomainFromStorage,
  buildUrlWithProtocol,
  formatDomain,
} from "./utils/domainUtils";
import getToken, {
  clearOrganizationTokenCache,
  recoverFromTokenError,
  getOrganizationToken,
  getOrgAccessToken,
} from "./utils/tokenUtils";
import { getConfigValue, getBasePath } from "./utils/runtimeConfig";

// Track auth requests globally
let authRequestInProgress = false;
let lastAuthRequestTime = 0;
const AUTH_REQUEST_DEBOUNCE_MS = 1000; // Debounce time between auth requests

// Store active sessions by domain
const activeSessions = new Map<string, boolean>();

// Cache for auth0 clients (domain only, no org)
const auth0ClientCache = new Map<string, Auth0Client>();

// Cache for management clients
const managementClientCache = new Map<string, ManagementClient>();

// Domain-prefixed localStorage cache so each domain gets isolated token storage
class PrefixedLocalStorageCache implements ICache {
  private prefix: string;

  constructor(domain: string) {
    this.prefix = `@@auth0spajs@@::${domain}::`;
  }

  set<T>(key: string, entry: T) {
    localStorage.setItem(`${this.prefix}${key}`, JSON.stringify(entry));
  }

  get<T>(key: string): T | undefined {
    const json = localStorage.getItem(`${this.prefix}${key}`);
    if (!json) return;
    try {
      return JSON.parse(json) as T;
    } catch {
      return;
    }
  }

  remove(key: string) {
    localStorage.removeItem(`${this.prefix}${key}`);
  }

  allKeys() {
    return Object.keys(localStorage)
      .filter((key) => key.startsWith(this.prefix))
      .map((key) => key.slice(this.prefix.length));
  }
}

// Create a function to get Auth0Client with the specified domain (no organization)
export const createAuth0Client = (domain: string) => {
  // Check cache first to avoid creating multiple clients for the same domain
  if (auth0ClientCache.has(domain)) {
    return auth0ClientCache.get(domain)!;
  }

  // Build full domain URL with HTTPS
  const fullDomain = buildUrlWithProtocol(domain);

  // Get redirect URI from current app URL
  const currentUrl = new URL(window.location.href);
  const redirectUri = `${currentUrl.protocol}//${currentUrl.host}${getBasePath()}/auth-callback`;

  // Use the management API audience for cross-tenant operations
  // This allows the admin UI to manage tenants and their resources
  const audience = getConfigValue("audience") || "urn:authhero:management";

  const clientId = getClientIdFromStorage(domain);

  const auth0Client = new Auth0Client({
    domain: fullDomain,
    clientId,
    cache: new PrefixedLocalStorageCache(formatDomain(domain)),
    useRefreshTokens: true,
    useRefreshTokensFallback: false,
    authorizationParams: {
      audience,
      redirect_uri: redirectUri,
      scope: "openid profile email",
    },
  });

  // Patch the loginWithRedirect method to prevent multiple calls
  const originalLoginWithRedirect =
    auth0Client.loginWithRedirect.bind(auth0Client);
  auth0Client.loginWithRedirect = async (options?: RedirectLoginOptions) => {
    const now = Date.now();

    // Check if we already have an active session
    const hasActiveSession = await auth0Client.isAuthenticated();
    if (hasActiveSession) {
      return Promise.resolve();
    }

    // Prevent multiple auth requests in parallel or in quick succession
    if (
      authRequestInProgress ||
      now - lastAuthRequestTime < AUTH_REQUEST_DEBOUNCE_MS
    ) {
      return Promise.resolve();
    }

    try {
      authRequestInProgress = true;
      lastAuthRequestTime = now;
      activeSessions.set(domain, true);
      return await originalLoginWithRedirect(options);
    } finally {
      // Reset after redirect completes or fails
      setTimeout(() => {
        authRequestInProgress = false;
      }, 1000); // Give a short delay to prevent immediate retries
    }
  };

  // Also patch the handleRedirectCallback to signal when the auth flow is complete
  const originalHandleRedirectCallback =
    auth0Client.handleRedirectCallback.bind(auth0Client);
  auth0Client.handleRedirectCallback = async (url?: string) => {
    try {
      const result = await originalHandleRedirectCallback(url);
      return result;
    } finally {
      // Mark that this domain's auth flow is complete
      activeSessions.delete(domain);
      authRequestInProgress = false;
    }
  };

  // Store in cache
  auth0ClientCache.set(domain, auth0Client);
  return auth0Client;
};

// Reads the `isSingleTenant` sessionStorage flag, but only honors it when the
// stored entry is for the same domain we're calling. The flag is written as
// `${domain}|${bool}`; a stale entry from a previously visited domain would
// otherwise steer multi-tenant requests to the non-org token path and pin a
// non-org token into managementClientCache for that tenant key.
export const isSingleTenantForDomain = (domain: string): boolean => {
  const storedFlag = sessionStorage.getItem("isSingleTenant");
  if (!storedFlag) return false;
  const sep = storedFlag.lastIndexOf("|");
  if (sep < 0) return false;
  const storedDomain = storedFlag.slice(0, sep);
  const storedValue = storedFlag.slice(sep + 1);
  return formatDomain(storedDomain) === domain && storedValue === "true";
};

// Create a Management API client
export const createManagementClient = async (
  apiUrl: string,
  tenantId?: string,
  oauthDomain?: string,
): Promise<ManagementClient> => {
  // Normalize tenant ID to lowercase to avoid casing mismatches
  const normalizedTenantId = tenantId?.toLowerCase();
  const oauthKey = oauthDomain ? formatDomain(oauthDomain) : apiUrl;
  const cacheKey = normalizedTenantId
    ? `${oauthKey}|${apiUrl}|${normalizedTenantId}`
    : `${oauthKey}|${apiUrl}`;

  // Check cache first
  if (managementClientCache.has(cacheKey)) {
    return managementClientCache.get(cacheKey)!;
  }

  // Use oauthDomain for finding credentials, fallback to apiUrl
  const domainForAuth = formatDomain(oauthDomain || apiUrl);
  const domains = getDomainFromStorage();
  const domainConfig = domains.find(
    (d) => formatDomain(d.url) === domainForAuth,
  );

  if (!domainConfig) {
    throw new Error(
      `No domain configuration found for domain: ${domainForAuth}`,
    );
  }

  // Pass the SDK a token supplier instead of a captured string. This way the
  // single-tenant flag and the org-vs-non-org decision are re-evaluated on
  // every request, and the underlying getOrgAccessToken/getNonOrgAccessToken
  // helpers handle their own short-TTL caching. Capturing a single token at
  // construction meant a non-org token (taken when the flag was momentarily
  // wrong) stuck around for the lifetime of the cached client.
  const tokenSupplier = async (): Promise<string> => {
    if (normalizedTenantId && !isSingleTenantForDomain(domainForAuth)) {
      if (domainConfig.connectionMethod === "login") {
        const auth0Client = createAuth0Client(domainForAuth);
        const audience =
          getConfigValue("audience") || "urn:authhero:management";

        try {
          return await getOrgAccessToken(
            auth0Client,
            normalizedTenantId,
            audience,
            domainForAuth,
          );
        } catch (error) {
          return await recoverFromTokenError(
            auth0Client,
            error,
            normalizedTenantId,
          );
        }
      }
      // For token/client_credentials, use getOrganizationToken
      return await getOrganizationToken(domainConfig, normalizedTenantId);
    }

    // No tenantId or single-tenant - non-org token
    let auth0Client: Auth0Client | undefined;
    if (domainConfig.connectionMethod === "login") {
      auth0Client = createAuth0Client(domainForAuth);
    }
    return await getToken(domainConfig, auth0Client);
  };

  // The SDK wrapper hardcodes `https://${domain}/api/v2`, which breaks
  // HTTP-only deployments (e.g. local Docker on http://localhost:3000).
  // The underlying Fern client reads `_options.baseUrl` lazily on every
  // request, so overriding it after super() takes effect for all calls.
  const url = new URL(apiUrl);
  const managementClient = new ManagementClient({
    domain: url.host,
    token: tokenSupplier,
    headers: normalizedTenantId
      ? { "tenant-id": normalizedTenantId }
      : undefined,
  });
  Object.assign(
    (managementClient as unknown as { _options: { baseUrl?: string } })
      ._options,
    { baseUrl: `${apiUrl.replace(/\/$/, "")}/api/v2` },
  );

  managementClientCache.set(cacheKey, managementClient);
  return managementClient;
};

/**
 * Resolve a bearer token for a tenant-scoped management call, applying the same
 * org-vs-non-org selection as {@link createManagementClient}'s token supplier.
 * Exposed so callers that need a *raw* `fetch` (e.g. streaming a binary export
 * download or uploading a file body) can authenticate without going through the
 * text-buffering JSON http client.
 */
export const resolveAccessToken = async (
  apiUrl: string,
  tenantId?: string,
  oauthDomain?: string,
): Promise<string> => {
  const normalizedTenantId = tenantId?.toLowerCase();
  const domainForAuth = formatDomain(oauthDomain || apiUrl);
  const domains = getDomainFromStorage();
  const domainConfig = domains.find(
    (d) => formatDomain(d.url) === domainForAuth,
  );

  if (!domainConfig) {
    throw new Error(
      `No domain configuration found for domain: ${domainForAuth}`,
    );
  }

  if (normalizedTenantId && !isSingleTenantForDomain(domainForAuth)) {
    if (domainConfig.connectionMethod === "login") {
      const auth0Client = createAuth0Client(domainForAuth);
      const audience = getConfigValue("audience") || "urn:authhero:management";
      try {
        return await getOrgAccessToken(
          auth0Client,
          normalizedTenantId,
          audience,
          domainForAuth,
        );
      } catch (error) {
        // Mirror createManagementClient's token supplier: when org-scoped
        // access is missing, redirect to login for the organization rather
        // than throwing, so export/import recovers the same way as the JSON
        // management client.
        return await recoverFromTokenError(
          auth0Client,
          error,
          normalizedTenantId,
        );
      }
    }
    return getOrganizationToken(domainConfig, normalizedTenantId);
  }

  const auth0Client =
    domainConfig.connectionMethod === "login"
      ? createAuth0Client(domainForAuth)
      : undefined;
  return getToken(domainConfig, auth0Client);
};

// Clear management client cache when token might be expired.
// Cache keys are `${oauthKey}|${apiUrl}[|${tenantId}]` (see cacheKey above),
// so callers that only have an OAuth domain can't selectively invalidate the
// matching apiUrl/tenant entries. Clear everything instead.
export const clearManagementClientCache = () => {
  managementClientCache.clear();
  clearOrganizationTokenCache();
};

// Create a function to get the auth provider with the specified domain
export const getAuthProvider = (
  domain: string,
  onAuthComplete?: () => void,
) => {
  // Get domain config to check connection method
  const domains = getDomainFromStorage();
  const formattedDomain = formatDomain(domain);
  const domainConfig = domains.find(
    (d) => formatDomain(d.url) === formattedDomain,
  );

  // If using token auth or client credentials, create a simple auth provider that uses the token
  if (
    ["token", "client_credentials"].includes(
      domainConfig?.connectionMethod || "",
    )
  ) {
    return {
      login: async () => {
        // Token auth is already authenticated
        return Promise.resolve();
      },
      logout: async () => {
        // Clear management client cache on logout
        clearManagementClientCache();
        return Promise.resolve();
      },
      checkError: async (error: { status?: number; statusCode?: number }) => {
        if (error.status === 401 || error.statusCode === 401) {
          clearManagementClientCache();
          return Promise.reject();
        }
        return Promise.resolve();
      },
      checkAuth: async () => {
        // Verify that credentials are actually available
        if (!domainConfig) {
          return Promise.reject(new Error("No domain configuration found"));
        }

        if (domainConfig.connectionMethod === "token" && !domainConfig.token) {
          return Promise.reject(
            new Error("Token authentication selected but no token provided"),
          );
        }

        if (
          domainConfig.connectionMethod === "client_credentials" &&
          (!domainConfig.clientId || !domainConfig.clientSecret)
        ) {
          return Promise.reject(
            new Error(
              "Client credentials authentication selected but credentials missing",
            ),
          );
        }

        return Promise.resolve();
      },
      getIdentity: async () => {
        // Return a dummy UserIdentity for token-based auth
        return Promise.resolve({
          id: "token-user",
          fullName: "API Token User",
          avatar: undefined,
        });
      },
      getPermissions: async () => {
        return Promise.resolve(["admin"]);
      },
    };
  }

  // For non-token auth, use Auth0
  const auth0 = createAuth0Client(domain);

  // Get the current app's URL for redirect
  const currentUrl = new URL(window.location.href);
  const basePath = getBasePath();
  const redirectUri = `${currentUrl.protocol}//${currentUrl.host}${basePath}`;

  const baseAuthProvider = Auth0AuthProvider(auth0, {
    // Use the current app's URL with the auth-callback path
    loginRedirectUri: `${redirectUri}/auth-callback`,
    // Use the current app's URL for logout
    logoutRedirectUri: redirectUri,
  });

  // Enhance the auth provider to signal when auth operations complete
  return {
    ...baseAuthProvider,
    login: async (params: Parameters<AuthProvider["login"]>[0]) => {
      try {
        const result = await baseAuthProvider.login(params);
        if (onAuthComplete) onAuthComplete();
        return result;
      } catch (error) {
        if (onAuthComplete) onAuthComplete();
        activeSessions.delete(domain);
        throw error;
      }
    },
    logout: async (params: Parameters<AuthProvider["logout"]>[0]) => {
      try {
        clearManagementClientCache();
        const result = await baseAuthProvider.logout(params);
        if (onAuthComplete) onAuthComplete();
        return result;
      } catch (error) {
        if (onAuthComplete) onAuthComplete();
        throw error;
      }
    },
    checkAuth: async (
      params: Parameters<AuthProvider["checkAuth"]>[0],
    ): Promise<void> => {
      try {
        // Don't check auth while on the callback page - we're in the middle of authenticating
        if (window.location.pathname === getBasePath() + "/auth-callback") {
          // Return success to prevent redirect loops during callback processing
          return Promise.resolve();
        }

        // If auth is in progress, wait for it to complete
        if (authRequestInProgress || activeSessions.has(domain)) {
          return new Promise<void>((resolve, reject) => {
            const checkInterval = setInterval(() => {
              if (!authRequestInProgress && !activeSessions.has(domain)) {
                clearInterval(checkInterval);
                // Re-check auth now that the redirect is complete
                baseAuthProvider.checkAuth(params).then(resolve).catch(reject);
              }
            }, 100);

            // Timeout after 30 seconds
            setTimeout(() => {
              clearInterval(checkInterval);
              reject(new Error("Authentication timeout"));
            }, 30000);
          });
        }

        await baseAuthProvider.checkAuth(params);
        return;
      } catch (error) {
        if (onAuthComplete) onAuthComplete();
        activeSessions.delete(domain);
        clearManagementClientCache();
        throw error;
      }
    },
  };
};

// Create auth provider for the selected domain
const authProvider = getAuthProvider(getSelectedDomainFromStorage());

// Create a debounced http client to prevent parallel token requests
type HttpResponse = Awaited<ReturnType<typeof fetchUtils.fetchJson>>;
const pendingRequests = new Map<string, Promise<HttpResponse>>();
export interface HttpOptions extends RequestInit {
  headers?: HeadersInit;
}

const authorizedHttpClient = (
  url: string,
  options: HttpOptions = {},
): Promise<HttpResponse> => {
  const requestKey = `${url}-${JSON.stringify(options)}`;

  // If there's already a pending request for this URL and options, return it
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey)!;
  }

  // Prevent API calls while on the auth callback page
  if (window.location.pathname === getBasePath() + "/auth-callback") {
    return Promise.reject({
      json: {
        message: "Authentication in progress. Please wait...",
      },
      status: 401,
      headers: new Headers(),
      body: JSON.stringify({ message: "Authentication in progress" }),
    });
  }

  // Check if we're using token-based auth or client credentials
  const domains = getDomainFromStorage();
  const selectedDomain = getSelectedDomainFromStorage();
  const formattedSelectedDomain = formatDomain(selectedDomain);
  const domainConfig = domains.find(
    (d) => formatDomain(d.url) === formattedSelectedDomain,
  );

  // If using login method and auth request is in progress, delay the request
  if (
    domainConfig?.connectionMethod === "login" &&
    (authRequestInProgress || activeSessions.has(formattedSelectedDomain))
  ) {
    // Return a promise that waits for auth to complete
    const delayedRequest = new Promise<HttpResponse>((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (
          !authRequestInProgress &&
          !activeSessions.has(formattedSelectedDomain)
        ) {
          clearInterval(checkInterval);
          // Retry the request now that auth is complete
          authorizedHttpClient(url, options).then(resolve).catch(reject);
        }
      }, 100);

      // Timeout after 30 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error("Authentication timeout"));
      }, 30000);
    });

    pendingRequests.set(requestKey, delayedRequest);
    delayedRequest.finally(() => {
      pendingRequests.delete(requestKey);
    });

    return delayedRequest;
  }

  // If no domain config found, throw an error
  if (!domainConfig) {
    return Promise.reject({
      json: {
        message:
          "No domain configuration found. Please select a domain and configure authentication.",
      },
      status: 401,
      headers: new Headers(),
      body: JSON.stringify({ message: "No domain configuration found" }),
    });
  }

  let request;
  if (
    ["token", "client_credentials"].includes(
      domainConfig.connectionMethod || "",
    )
  ) {
    // For token auth or client credentials, use the getToken helper
    request = getToken(domainConfig)
      .catch((error) => {
        // If we can't get a token, throw a more helpful error
        throw new Error(
          `Authentication failed: ${error.message}. Please configure your credentials in the domain selector.`,
        );
      })
      .then((token) => {
        let headersObj: Headers;
        const method = (options.method || "GET").toUpperCase();
        if (method === "GET") {
          // Only send Authorization for GET to avoid CORS issues
          headersObj = new Headers();
          headersObj.set("Authorization", `Bearer ${token}`);
        } else if (
          method === "POST" ||
          method === "DELETE" ||
          method === "PATCH"
        ) {
          // For POST, DELETE, PATCH: only send Authorization and content-type (force application/json for POST/PATCH)
          headersObj = new Headers();
          headersObj.set("Authorization", `Bearer ${token}`);
          if (method === "POST" || method === "PATCH") {
            headersObj.set("content-type", "application/json");
          }
        } else {
          // For other methods, merge all headers and set Authorization
          headersObj = new Headers(options.headers || {});
          headersObj.set("Authorization", `Bearer ${token}`);
        }
        return fetch(url, { ...options, headers: headersObj });
      })
      .then(async (response) => {
        if (response.status < 200 || response.status >= 300) {
          const text = await response.text();

          // Check for 401 with "Bad audience" message on /v2/tenants endpoint - Auth0 doesn't support this endpoint
          if (
            response.status === 401 &&
            text.includes("Bad audience") &&
            url.includes("/v2/tenants")
          ) {
            console.warn(
              "Auth0 server detected without multi-tenant support. Navigating to Auth0 page",
            );
            // Clean up pending request to prevent deadlock
            pendingRequests.delete(requestKey);
            // Use history.pushState for a soft navigation instead of a hard redirect
            window.history.pushState({}, "", "/auth0");
            // Dispatch a navigation event so the app can respond to the URL change
            window.dispatchEvent(new PopStateEvent("popstate"));
            // Reject with a clear error message instead of hanging
            throw new Error("Navigating to Auth0 configuration");
          }

          throw new Error(response.statusText);
        }

        const text = await response.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          json = text;
        }

        // Return in the format expected by react-admin's dataProvider
        return {
          json,
          status: response.status,
          headers: response.headers,
          body: text,
        };
      })
      .catch((error) => {
        throw error;
      });
  } else {
    // For Auth0 login method, get a token WITHOUT organization scope
    // This ensures we don't accidentally use an org-scoped token when listing tenants
    const currentAuth0Client = createAuth0Client(selectedDomain);
    request = getToken(domainConfig, currentAuth0Client)
      .catch((error) => {
        throw new Error(
          `Authentication failed: ${error.message}. Please log in again.`,
        );
      })
      .then((token) => {
        const headersObj = new Headers();
        headersObj.set("Authorization", `Bearer ${token}`);
        const method = (options.method || "GET").toUpperCase();
        if (method === "POST" || method === "PATCH") {
          headersObj.set("content-type", "application/json");
        }
        return fetch(url, { ...options, headers: headersObj });
      })
      .then(async (response) => {
        if (response.status < 200 || response.status >= 300) {
          const text = await response.text();

          // Check for 401 with "Bad audience" message on /v2/tenants endpoint
          if (
            response.status === 401 &&
            text.includes("Bad audience") &&
            url.includes("/v2/tenants")
          ) {
            console.warn(
              "Auth0 server detected without multi-tenant support. Navigating to Auth0 page",
            );
            pendingRequests.delete(requestKey);
            window.history.pushState({}, "", "/auth0");
            window.dispatchEvent(new PopStateEvent("popstate"));
            throw new Error("Navigating to Auth0 configuration");
          }

          throw new Error(text || response.statusText);
        }

        const text = await response.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          json = text;
        }

        return {
          json,
          status: response.status,
          headers: response.headers,
          body: text,
        };
      })
      .catch((error) => {
        if (error.message === "Failed to fetch" || error.name === "TypeError") {
          const urlObj = new URL(url);
          if (
            urlObj.hostname === "localhost" ||
            urlObj.hostname === "127.0.0.1"
          ) {
            const certError = new Error(
              `Unable to connect to ${urlObj.origin}. This may be due to an untrusted SSL certificate.`,
            );
            Object.assign(certError, {
              isCertificateError: true,
              serverUrl: urlObj.origin,
            });
            throw certError;
          }
        }
        throw error;
      });
  }

  // Handle cleanup when request is done
  request.finally(() => {
    pendingRequests.delete(requestKey);
  });

  // Cache the request
  pendingRequests.set(requestKey, request);
  return request;
};

/**
 * Creates an HTTP client that uses organization-scoped tokens.
 * This is used when accessing tenant-specific resources to ensure
 * the user has the correct permissions for that tenant.
 *
 * @param organizationId - The organization/tenant ID to scope tokens to
 * @returns An HTTP client function that uses organization-scoped tokens
 */
export const createOrganizationHttpClient = (organizationId: string) => {
  // Normalize organization ID to lowercase to avoid casing mismatches
  const normalizedOrgId = organizationId.toLowerCase();

  return (url: string, options: HttpOptions = {}) => {
    const requestKey = `${normalizedOrgId}:${url}-${JSON.stringify(options)}`;

    // If there's already a pending request for this URL and options, return it
    if (pendingRequests.has(requestKey)) {
      return pendingRequests.get(requestKey)!;
    }

    // Prevent API calls while on the auth callback page
    if (window.location.pathname === getBasePath() + "/auth-callback") {
      return Promise.reject({
        json: {
          message: "Authentication in progress. Please wait...",
        },
        status: 401,
        headers: new Headers(),
        body: JSON.stringify({ message: "Authentication in progress" }),
      });
    }

    // Check if we're using token-based auth or client credentials
    const domains = getDomainFromStorage();
    const selectedDomain = getSelectedDomainFromStorage();
    const formattedSelectedDomain = formatDomain(selectedDomain);
    const domainConfig = domains.find(
      (d) => formatDomain(d.url) === formattedSelectedDomain,
    );

    // If using login method and auth request is in progress, delay the request
    if (
      domainConfig?.connectionMethod === "login" &&
      (authRequestInProgress || activeSessions.has(formattedSelectedDomain))
    ) {
      const delayedRequest = new Promise<HttpResponse>((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (
            !authRequestInProgress &&
            !activeSessions.has(formattedSelectedDomain)
          ) {
            clearInterval(checkInterval);
            createOrganizationHttpClient(normalizedOrgId)(url, options)
              .then(resolve)
              .catch(reject);
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Authentication timeout"));
        }, 30000);
      });

      pendingRequests.set(requestKey, delayedRequest);
      delayedRequest.finally(() => {
        pendingRequests.delete(requestKey);
      });

      return delayedRequest;
    }

    if (!domainConfig) {
      return Promise.reject({
        json: {
          message:
            "No domain configuration found. Please select a domain and configure authentication.",
        },
        status: 401,
        headers: new Headers(),
        body: JSON.stringify({ message: "No domain configuration found" }),
      });
    }

    // Both connection methods differ only in how the org-scoped token is
    // obtained; once we have the token promise, building the request headers,
    // parsing the response and mapping a failed-fetch to a certificate error
    // are identical. Share that tail here.
    const sendWithToken = (tokenPromise: Promise<string>) =>
      tokenPromise
        .then((token) => {
          const headersObj = new Headers();
          // Merge any headers passed in options
          if (options.headers) {
            const incomingHeaders =
              options.headers instanceof Headers
                ? options.headers
                : new Headers(options.headers as HeadersInit);
            incomingHeaders.forEach((value, key) => {
              headersObj.set(key, value);
            });
          }
          // Carry the tenant context for apex/loopback deployments where the
          // server resolves the tenant from this header rather than the host.
          // Set after merging caller headers so it can't be omitted or
          // overridden; the org id is the tenant id here.
          headersObj.set("tenant-id", normalizedOrgId);
          headersObj.set("Authorization", `Bearer ${token}`);
          const method = (options.method || "GET").toUpperCase();
          if (method === "POST" || method === "PATCH") {
            headersObj.set("content-type", "application/json");
          }
          return fetch(url, { ...options, headers: headersObj });
        })
        .then(async (response) => {
          if (response.status < 200 || response.status >= 300) {
            const text = await response.text();
            throw new Error(text || response.statusText);
          }

          const text = await response.text();
          let json;
          try {
            json = JSON.parse(text);
          } catch {
            json = text;
          }

          return {
            json,
            status: response.status,
            headers: response.headers,
            body: text,
          };
        })
        .catch((error) => {
          if (
            error.message === "Failed to fetch" ||
            error.name === "TypeError"
          ) {
            const urlObj = new URL(url);
            if (
              urlObj.hostname === "localhost" ||
              urlObj.hostname === "127.0.0.1"
            ) {
              const certError = new Error(
                `Unable to connect to ${urlObj.origin}. This may be due to an untrusted SSL certificate.`,
              );
              Object.assign(certError, {
                isCertificateError: true,
                serverUrl: urlObj.origin,
              });
              throw certError;
            }
          }
          throw error;
        });

    let request;
    if (
      ["token", "client_credentials"].includes(
        domainConfig.connectionMethod || "",
      )
    ) {
      // For token/client_credentials, use organization-scoped token
      // This includes the org_id claim for accessing tenant-specific resources
      request = sendWithToken(
        getOrganizationToken(domainConfig, normalizedOrgId).catch((error) => {
          throw new Error(
            `Authentication failed: ${error.message}. Please configure your credentials in the domain selector.`,
          );
        }),
      );
    } else {
      // For OAuth login, use refresh token with organization parameter
      const auth0Client = createAuth0Client(selectedDomain);
      const audience = getConfigValue("audience") || "urn:authhero:management";

      request = sendWithToken(
        getOrgAccessToken(
          auth0Client,
          normalizedOrgId,
          audience,
          formattedSelectedDomain,
        ).catch(async (error) =>
          recoverFromTokenError(auth0Client, error, normalizedOrgId),
        ),
      );
    }

    request.finally(() => {
      pendingRequests.delete(requestKey);
    });

    pendingRequests.set(requestKey, request);
    return request;
  };
};

export { authProvider, authorizedHttpClient };
